import React from 'react'
import { useNavigate } from 'react-router-dom'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../../../components/Common/PaginationBar'

interface Props {
    rooms: any[]
    pcbs: any[]
    relays: any[]
    devices: any[]
    owners: any[]
    members: any[]
    placeId: string
    userId: string
}

const StatTile = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
    <div style={{
        flex: 1, background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8,
        padding: '10px 24px', minWidth: 160,
    }}>
        <p style={{ color: '#858D9D', fontSize: 13, marginBottom: 2 }}>{label}</p>
        <h3 style={{ fontSize: 22, fontWeight: 700, margin: 0, lineHeight: 1.2 }}>{value}</h3>
        {sub && <p style={{ color: '#858D9D', fontSize: 12, marginTop: 2, marginBottom: 0 }}>{sub}</p>}
    </div>
)

const Overview = ({ rooms, pcbs, relays, devices, owners, members, placeId, userId }: Props) => {
    const navigate = useNavigate()
    const [page, setPage] = usePersistedPage(`place-overview-rooms-${placeId}`)

    const placeRooms = (rooms || []).filter(r => r.placeId === placeId)
    const placeRelays = (relays || []).filter(r => r.placeId === placeId)
    const placeDevices = (devices || []).filter(d => d.placeId === placeId)
    const placePcbs = (pcbs || []).filter(p => p.placeId === placeId)
    // Dedupe by id — same user may appear in both owners and members
    const uniqueMemberIds = new Set<string>([
        ...owners.map((u: any) => u?.id).filter(Boolean),
        ...members.map((u: any) => u?.id).filter(Boolean),
    ])
    const totalMembers = uniqueMemberIds.size

    const roomRows = placeRooms.map(room => ({
        id: room.id,
        name: room.name,
        modules: placePcbs.filter(p => p.roomId === room.id).length,
        appliances: placeDevices.filter(d => d.roomId === room.id).length,
        standalone: placePcbs.filter(p => !p.roomId || p.roomId === room.id && !room.id).length,
    }))

    const standaloneModules = placePcbs.filter(p => !p.roomId).length
    const { paged, totalPages, safePage } = paginate(roomRows, page)

    return (
        <div>
            {/* Stat tiles */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                <StatTile label="Rooms" value={placeRooms.length} />
                <StatTile label="Appliances" value={placeDevices.length} />
                <StatTile label="Members" value={totalMembers} />
                <StatTile label="Scenes" value="--" sub="coming soon" />
                <StatTile label="Automations" value="--" sub="coming soon" />
            </div>

            {/* Room snapshot table */}
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #E4E7EC' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>Rooms</h6>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                            {['Room', 'Modules', 'Appliances'].map(h => (
                                <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {roomRows.length === 0 && (
                            <tr>
                                <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#858D9D', fontSize: 14 }}>No rooms found</td>
                            </tr>
                        )}
                        {paged.map((row, idx) => (
                            <tr key={row.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid #E4E7EC' }}>
                                <td style={{ padding: '12px 20px' }}>
                                    <button
                                        onClick={() => navigate(`/users/profile/${userId}/places/${placeId}/rooms/${row.id}`)}
                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1a73e8', fontSize: 14, fontWeight: 500, textDecoration: 'underline' }}
                                    >
                                        {row.name}
                                    </button>
                                </td>
                                <td style={{ padding: '12px 20px', fontSize: 14, color: '#344054' }}>{row.modules}</td>
                                <td style={{ padding: '12px 20px', fontSize: 14, color: '#344054' }}>{row.appliances}</td>
                            </tr>
                        ))}
                        {standaloneModules > 0 && (
                            <tr style={{ borderTop: '1px solid #E4E7EC', background: '#FAFAFA' }}>
                                <td style={{ padding: '12px 20px', fontSize: 14, color: '#858D9D', fontStyle: 'italic' }}>Standalone</td>
                                <td style={{ padding: '12px 20px', fontSize: 14, color: '#344054' }}>{standaloneModules}</td>
                                <td style={{ padding: '12px 20px', fontSize: 14, color: '#344054' }}>--</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <PaginationBar
                    page={safePage}
                    totalPages={totalPages}
                    total={roomRows.length}
                    onPage={setPage}
                />
            </div>
        </div>
    )
}

export default Overview
